import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { GiftPassBundleApiError } from '../../api/gift-pass-bundles';
import { EditorPageNotice } from '../shared/editor-page/EditorPageNotice';
import { useJustCreated } from '../shared/editor-page/use-just-created';

import { bundleToForm, toPatchRequest } from './editor/bundle-form';
import { GiftPassBundleEditor } from './editor/GiftPassBundleEditor';
import { GIFT_PASS_BUNDLES_LIST_PATH } from './gift-pass-bundles-paths';
import { useGiftPassBundle, useUpdateGiftPassBundle } from './use-gift-pass-bundles';

const NOTICE = {
  listPath: GIFT_PASS_BUNDLES_LIST_PATH,
  listLabel: 'Gift pass bundles',
  title: 'Edit bundle',
};

function loadProblem(error: unknown): string {
  if (error instanceof GiftPassBundleApiError && error.status === 404) {
    return 'This bundle doesn’t exist. The link may be wrong.';
  }
  if (error instanceof GiftPassBundleApiError && error.status === 403) {
    return 'You don’t have permission to manage bundles.';
  }
  if (error instanceof GiftPassBundleApiError && error.status === 400) {
    return 'This link doesn’t point to a bundle.';
  }
  return 'This bundle couldn’t be loaded. Refresh the page to try again.';
}

export default function GiftPassBundleEditPage() {
  const { id = '' } = useParams<{ id: string }>();
  const query = useGiftPassBundle(id);
  const update = useUpdateGiftPassBundle(id);
  const created = useJustCreated();
  const bundle = query.data;
  // Built once per loaded bundle, not on every render.
  const initial = useMemo(() => (bundle ? bundleToForm(bundle) : null), [bundle]);

  if (query.isError) {
    return <EditorPageNotice message={loadProblem(query.error)} {...NOTICE} problem />;
  }
  if (!bundle || !initial) return <EditorPageNotice message="Loading the bundle…" {...NOTICE} />;

  return (
    <GiftPassBundleEditor
      key={bundle.id}
      bundle={bundle}
      initial={initial}
      initiallySaved={created}
      persist={(values, base) => {
        const patch = toPatchRequest(base, values);
        return patch ? update.mutateAsync(patch) : Promise.resolve(bundle);
      }}
    />
  );
}
